/**
 * Lightweight hover/focus tooltip for icon-only buttons.
 *
 *     <Tooltip label="View audit trail">
 *       <button aria-label="View audit trail" onClick={…}>
 *         <History size={16} />
 *       </button>
 *     </Tooltip>
 *
 * Wraps a single interactive child. The child should still carry its own
 * `aria-label` — the tooltip is a visual affordance, not the accessible
 * name. CSS-driven (group-hover/focus) so there's no JS positioning cost.
 */
import { type ReactElement } from "react";

import { cn } from "@/lib/utils";

export function Tooltip({
  label,
  side = "top",
  children,
}: {
  label: string;
  side?: "top" | "bottom";
  children: ReactElement;
}) {
  return (
    <span className="group/tt relative inline-flex">
      {children}
      <span
        role="tooltip"
        className={cn(
          "pointer-events-none absolute left-1/2 z-40 -translate-x-1/2 whitespace-nowrap rounded bg-navy px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-md transition-opacity duration-100 group-hover/tt:opacity-100 group-focus-within/tt:opacity-100",
          side === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
        )}
      >
        {label}
      </span>
    </span>
  );
}
